import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Play, Users, ArrowRight } from "lucide-react";
import { useRadio } from "@/lib/RadioContext";
import { CHANNEL_META } from "@/components/RadioChannels";
import { EP_BY_SLUG, channelListeners } from "@/pages/Radio";

const L = {
  fr: { live: "En direct", title: "Radio Cocoon", now: "En ce moment", jingle: "Petite respiration entre deux épisodes", listen: "Écouter le direct", listening: "Tu écoutes", listeners: "esprits à l'écoute" },
  en: { live: "Live", title: "Radio Cocoon", now: "Right now", jingle: "A short breath between two episodes", listen: "Listen live", listening: "You're listening", listeners: "minds listening" },
  es: { live: "En directo", title: "Radio Cocoon", now: "Ahora mismo", jingle: "Una breve pausa entre dos episodios", listen: "Escuchar en directo", listening: "Estás escuchando", listeners: "mentes escuchando" },
  nl: { live: "Live", title: "Radio Cocoon", now: "Nu te horen", jingle: "Even ademhalen tussen twee afleveringen", listen: "Luister live", listening: "Je luistert", listeners: "geesten luisteren" },
};

export const RadioLiveCard = () => {
  const { i18n } = useTranslation();
  const lang = ["fr", "en", "es", "nl"].includes((i18n.language || "fr").slice(0, 2)) ? (i18n.language || "fr").slice(0, 2) : "fr";
  const T = L[lang];
  const r = useRadio();
  useEffect(() => { r.ensureLoaded(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ep = r.pos ? EP_BY_SLUG[r.currentSlug] : null;
  const isJingle = r.pos?.kind === "jingle";

  return (
    <section className="relative max-w-5xl mx-auto px-6 pt-4 pb-10" data-testid="radio-live-card">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.8 }}
        className="glass rounded-3xl border border-primary/20 p-5 sm:p-8 grid sm:grid-cols-[180px_1fr] gap-6 sm:gap-8 items-center shadow-[0_20px_80px_-30px_hsl(28_96%_56%_/_0.35)]">
        {ep && (
          <img src={isJingle ? "/derive/radio/cover.jpg" : `/derive/covers/${ep.slug}.jpg`} alt={ep.title[lang] || ep.title.fr}
            className="w-full max-w-[200px] mx-auto aspect-square rounded-2xl object-cover border border-primary/25 shadow-2xl" data-testid="radio-live-card-cover" />
        )}
        <div className="min-w-0">
          <span className="inline-flex items-center gap-2 rounded-full bg-red-500/15 border border-red-400/40 px-3 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.24em] text-red-300 mb-3">
            <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping" /><span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" /></span>
            {T.live} · {CHANNEL_META[r.channel].label[lang]}
          </span>
          <h3 className="font-display text-3xl sm:text-4xl leading-tight mb-2">{T.title}</h3>
          {ep && (
            <p className="text-foreground/70 text-sm sm:text-base mb-5" data-testid="radio-live-card-now">
              <span className="text-primary/80">{r.playing ? T.listening : T.now} · </span>{isJingle ? T.jingle : ep.title[lang] || ep.title.fr}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-4">
            <Link to="/radio" data-testid="radio-live-card-listen-btn"
              className="btn-frost-orange group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold">
              <Play size={15} /> {T.listen} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </Link>
            <span className="inline-flex items-center gap-1.5 text-xs text-foreground/60" data-testid="radio-live-card-listeners">
              <Users size={14} className="text-primary/80" /> <b className="text-foreground/85 tabular-nums">{channelListeners(r.channel, r.now)}</b> {T.listeners}
            </span>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
